import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num, shortDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const REASONS = { variazione: "Variazione quantità", fallati: "Capi fallati", altro: "Altro" };
export const CAUSES = { tessuto: "Difetto tessuto (imperfezioni/impurità)", cucitura: "Errore cucitura (laboratorio)", altro: "Altro" };

export const EditQuantitiesButton = ({ batch }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const current = Object.fromEntries((batch.sizes || []).map((s) => [s.size, s.quantity]));
  const [qty, setQty] = useState(current);
  const qc_ = ["controllo", "completato"].includes(batch.phase);
  const [reason, setReason] = useState(qc_ ? "fallati" : "variazione");
  const [note, setNote] = useState("");
  const [cause, setCause] = useState("tessuto");
  const [fabric, setFabric] = useState("");
  const { data: materials = [] } = useQuery({ queryKey: ["materials"], enabled: open && reason === "fallati", queryFn: async () => (await api.get("/materials")).data });
  const fabrics = materials.filter((m) => m.type === "tessuto");
  const { data: products = [] } = useQuery({ queryKey: ["products"], enabled: open, queryFn: async () => (await api.get("/products")).data });
  const productSizes = (products.find((p) => p.id === batch.product_id)?.sizes || []).map((s) => s.size);
  const sizes = [...new Set([...productSizes, ...Object.keys(current)])];
  const total = sizes.reduce((s, k) => s + (Number(qty[k]) || 0), 0);

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/production/${batch.id}`, {
      sizes: sizes.map((size) => ({ size, quantity: Number(qty[size]) || 0 })), change_reason: reason, change_note: note || null,
      ...(reason === "fallati" ? { defect_cause: cause, defect_material_id: cause === "tessuto" && fabric ? fabric : null } : {}),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["production"] }); toast.success("Quantità aggiornate"); setOpen(false); setNote(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setQty(current); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground" title="Modifica quantità" data-testid={`edit-qty-${batch.batch_number}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Modifica quantità · {batch.batch_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{batch.product_name}{batch.color ? ` · ${batch.color}` : ""}</p>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {sizes.map((s) => (
            <div key={s} className="space-y-1">
              <Label className="text-xs text-muted-foreground">{s}</Label>
              <Input type="number" min="0" value={qty[s] ?? ""} onChange={(e) => setQty({ ...qty, [s]: e.target.value })} data-testid={`edit-size-${s}`} />
            </div>
          ))}
        </div>
        <p className="text-right text-xs text-muted-foreground" data-testid="edit-qty-total">Totale: {num(total)} pz (prima {num(batch.quantity)})</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Motivo</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="edit-qty-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(REASONS).filter(([k]) => qc_ || k !== "fallati").map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {reason === "fallati" && (
            <div className="space-y-1.5">
              <Label>Causa</Label>
              <Select value={cause} onValueChange={setCause}>
                <SelectTrigger data-testid="edit-qty-cause"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CAUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {reason === "fallati" && cause === "tessuto" && (
            <div className="col-span-2 space-y-1.5">
              <Label>Tessuto difettoso</Label>
              <Select value={fabric} onValueChange={setFabric}>
                <SelectTrigger data-testid="edit-qty-fabric"><SelectValue placeholder={fabrics.length ? "Scegli il tessuto" : "Nessun tessuto in anagrafica"} /></SelectTrigger>
                <SelectContent>{fabrics.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}{f.color ? ` · ${f.color}` : ""}{f.supplier ? ` — ${f.supplier}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {reason === "fallati" && cause === "cucitura" && (
            <p className="col-span-2 text-xs text-muted-foreground" data-testid="edit-qty-lab">Laboratorio: {batch.lab || "non indicato sul lotto"}</p>
          )}
          <div className="col-span-2 space-y-1.5"><Label>Nota (facoltativa)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="edit-qty-note" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => save.mutate()} disabled={save.isPending || !total} data-testid="edit-qty-save">
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Salva
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const QuantityChanges = ({ batch, phaseMeta }) => {
  if (!batch.changes?.length) return null;
  const planned = (batch.planned_sizes || []).reduce((s, x) => s + x.quantity, 0);
  const defects = batch.changes.filter((c) => c.reason === "fallati").flatMap((c) => c.diff).reduce((s, d) => s + Math.max(0, d.from - d.to), 0);
  return (
    <div className="mt-3 space-y-1.5 rounded-md border border-border/60 p-3 text-xs" data-testid={`qty-changes-${batch.batch_number}`}>
      <div className="flex justify-between font-mono-erp text-[11px] uppercase tracking-widest text-muted-foreground">
        <span>Modifiche quantità</span>
        <span>programmati {num(planned)} → {num(batch.quantity)}{defects ? ` · fallati ${num(defects)}` : ""}</span>
      </div>
      {batch.changes.map((c) => (
        <div key={`${c.date}-${c.phase}-${c.reason}`}>
          <span className={c.reason === "fallati" ? "text-rose-400" : ""}>{REASONS[c.reason] || c.reason}</span>
          <span className="text-muted-foreground"> · {phaseMeta(c.phase).label} · {shortDate(c.date)}</span>
          <p className="text-muted-foreground">{c.diff.map((d) => `${d.size}: ${d.from}→${d.to}`).join(", ")}{c.note ? ` — ${c.note}` : ""}</p>
        </div>
      ))}
    </div>
  );
};
